import React, { useState } from 'react';
import { useContract } from '../../context/ContractContext';

const ClausesForm = () => {
  const { contractData, updateContractData } = useContract();
  const { clauses } = contractData;
  const [customClause, setCustomClause] = useState('');

  const standardClauses = {
    locacao: [
      {
        id: 'prazo',
        title: 'Prazo do Contrato',
        content: 'O presente contrato terá vigência pelo prazo determinado estabelecido, iniciando-se na data de assinatura e encerrando-se automaticamente ao final do período, independentemente de notificação.'
      },
      {
        id: 'uso',
        title: 'Destinação do Imóvel',
        content: 'O imóvel destina-se exclusivamente para uso residencial, sendo vedada sua utilização para fins comerciais ou industriais sem prévia autorização por escrito do locador.'
      },
      {
        id: 'conservacao',
        title: 'Conservação do Imóvel',
        content: 'O locatário obriga-se a conservar o imóvel em bom estado, responsabilizando-se pelos danos que causar por uso inadequado ou negligência.'
      },
      {
        id: 'obras',
        title: 'Obras e Benfeitorias',
        content: 'Qualquer obra ou benfeitoria no imóvel depende de prévia autorização por escrito do locador, não gerando direito de indenização ou retenção.'
      },
      {
        id: 'animais',
        title: 'Animais Domésticos',
        content: 'É vedada a permanência de animais no imóvel sem prévia autorização por escrito do locador.'
      },
      {
        id: 'sublocacao',
        title: 'Sublocação',
        content: 'É vedada a sublocação total ou parcial do imóvel sem prévia autorização por escrito do locador.'
      },
      {
        id: 'vistoria',
        title: 'Vistoria',
        content: 'O locador tem o direito de vistoriar o imóvel mediante agendamento prévio com o locatário.'
      }
    ],
    'compra-venda': [
      {
        id: 'transferencia',
        title: 'Transferência de Propriedade',
        content: 'A transferência da propriedade do imóvel se dará com o pagamento integral do preço e a devida escritura pública.'
      },
      {
        id: 'estado',
        title: 'Estado do Imóvel',
        content: 'O comprador declara conhecer o estado atual do imóvel, que lhe é entregue no estado em que se encontra.'
      },
      {
        id: 'documentacao',
        title: 'Documentação',
        content: 'O vendedor obriga-se a entregar toda a documentação necessária para a transferência da propriedade.'
      },
      {
        id: 'impostos',
        title: 'Impostos e Taxas',
        content: 'Os impostos, taxas e contribuições incidentes sobre o imóvel, vencidos até a data da transferência, são de responsabilidade do vendedor.'
      }
    ]
  };

  const getCurrentStandardClauses = () => {
    return standardClauses[contractData.type] || [];
  };

  const handleStandardClauseToggle = (clauseId, checked) => {
    const currentClauses = clauses.standardClauses || [];
    let newClauses;

    if (checked) {
      newClauses = [...currentClauses, clauseId];
    } else {
      newClauses = currentClauses.filter(id => id !== clauseId);
    }

    updateContractData('clauses', {
      ...clauses,
      standardClauses: newClauses
    });
  };

  const addCustomClause = () => {
    if (!customClause.trim()) return;

    const newCustomClauses = [
      ...(clauses.customClauses || []),
      {
        id: Date.now().toString(),
        content: customClause.trim()
      }
    ];

    updateContractData('clauses', {
      ...clauses,
      customClauses: newCustomClauses
    });

    setCustomClause('');
  };

  const removeCustomClause = (clauseId) => {
    const newCustomClauses = (clauses.customClauses || []).filter(
      clause => clause.id !== clauseId
    );

    updateContractData('clauses', {
      ...clauses,
      customClauses: newCustomClauses
    });
  };

  const editCustomClause = (clauseId, newContent) => {
    const newCustomClauses = (clauses.customClauses || []).map(clause =>
      clause.id === clauseId ? { ...clause, content: newContent } : clause
    );

    updateContractData('clauses', {
      ...clauses,
      customClauses: newCustomClauses
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-semibold text-dark-900 mb-6">
          Cláusulas Contratuais
        </h2>

        {/* Cláusulas Padrão */}
        <div className="card p-6 mb-6">
          <h3 className="text-lg font-semibold text-dark-900 mb-4">
            Cláusulas Padrão
          </h3>
          <p className="text-sm text-neutral-600 mb-4">
            Selecione as cláusulas padrão que deseja incluir no contrato:
          </p>

          <div className="space-y-4">
            {getCurrentStandardClauses().map((clause) => (
              <div key={clause.id} className="border border-neutral-200 rounded-lg p-4">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id={`clause-${clause.id}`}
                    checked={(clauses.standardClauses || []).includes(clause.id)}
                    onChange={(e) => handleStandardClauseToggle(clause.id, e.target.checked)}
                    className="h-4 w-4 text-primary-500 focus:ring-primary-500 border-neutral-300 rounded mt-1"
                  />
                  <div className="ml-3 flex-1">
                    <label htmlFor={`clause-${clause.id}`} className="font-medium text-dark-900 cursor-pointer">
                      {clause.title}
                    </label>
                    <p className="text-sm text-neutral-600 mt-1">
                      {clause.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Cláusulas Personalizadas */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-dark-900 mb-4">
            Cláusulas Personalizadas
          </h3>

          {/* Adicionar nova cláusula */}
          <div className="mb-6">
            <label htmlFor="custom-clause" className="block text-sm font-medium text-dark-800 mb-2">
              Nova Cláusula Personalizada
            </label>
            <div className="flex gap-3">
              <textarea
                id="custom-clause"
                rows={3}
                value={customClause}
                onChange={(e) => setCustomClause(e.target.value)}
                className="input flex-1"
                placeholder="Digite aqui uma cláusula personalizada..."
              />
              <button
                type="button"
                onClick={addCustomClause}
                disabled={!customClause.trim()}
                className="btn btn-primary self-start"
              >
                Adicionar
              </button>
            </div>
          </div>

          {/* Lista de cláusulas personalizadas */}
          <div className="space-y-4">
            {(clauses.customClauses || []).map((clause, index) => (
              <div key={clause.id} className="border border-neutral-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-medium text-dark-900">
                    Cláusula Personalizada {index + 1}
                  </h4>
                  <button
                    type="button"
                    onClick={() => removeCustomClause(clause.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remover
                  </button>
                </div>
                <textarea
                  rows={3}
                  value={clause.content}
                  onChange={(e) => editCustomClause(clause.id, e.target.value)}
                  className="input"
                />
              </div>
            ))}

            {(!clauses.customClauses || clauses.customClauses.length === 0) && (
              <p className="text-neutral-500 text-sm italic">
                Nenhuma cláusula personalizada adicionada
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClausesForm;